package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.CSSSizeValue
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.HTMLStyleElement
import org.w3c.dom.HTMLTextAreaElement

enum class DiffRowType {
    ADDED, REMOVED, UNCHANGED
}

data class DiffLine(
    val type: DiffRowType,
    val text: String,
    val oldLine: Int?,
    val newLine: Int?
)

fun computeUnifiedDiff(oldCode: String, newCode: String): List<DiffLine> {
    val oldLines = oldCode.lines()
    val newLines = newCode.lines()
    val n = oldLines.size
    val m = newLines.size

    val dp = Array(n + 1) { IntArray(m + 1) }

    for (i in 1..n) {
        val oldTrimmed = oldLines[i - 1].trim()
        for (j in 1..m) {
            val newTrimmed = newLines[j - 1].trim()
            if (oldTrimmed == newTrimmed) {
                dp[i][j] = dp[i - 1][j - 1] + 1
            } else {
                dp[i][j] = maxOf(dp[i - 1][j], dp[i][j - 1])
            }
        }
    }

    val result = mutableListOf<DiffLine>()
    var i = n
    var j = m

    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1].trim() == newLines[j - 1].trim()) {
            result.add(DiffLine(DiffRowType.UNCHANGED, newLines[j - 1], i, j))
            i--
            j--
        } else if (j > 0 && (i == 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            result.add(DiffLine(DiffRowType.ADDED, newLines[j - 1], null, j))
            j--
        } else if (i > 0) {
            result.add(DiffLine(DiffRowType.REMOVED, oldLines[i - 1], i, null))
            i--
        }
    }

    result.reverse()
    return result
}

val CodeViewerContainerStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .borderRadius(0.75.cssRem)
        .backgroundColor(palette.surfaceElevated)
        .border(1.px, LineStyle.Solid, palette.border)
        .overflow(Overflow.Hidden)
        .transition(
            Transition.of("border-color", 200.ms),
            Transition.of("background-color", 200.ms)
        )
}

val CodeViewerHeaderStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
        .backgroundColor(palette.surface)
        .borderBottom(1.px, LineStyle.Solid, palette.border)
}



fun ensurePrismLoaded() {
    try {
        if (document.getElementById("prism-tomorrow-css") == null) {
            val link = document.createElement("link") as HTMLLinkElement
            link.id = "prism-tomorrow-css"
            link.rel = "stylesheet"
            link.href = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css"
            document.head?.appendChild(link)
        }
        if (document.getElementById("prism-custom-theme") == null) {
            val style = document.createElement("style") as HTMLStyleElement
            style.id = "prism-custom-theme"
            style.textContent = """
                .code-viewer-pre {
                    background: transparent !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    border-radius: 0 !important;
                    overflow: visible !important;
                }
                .code-viewer-code, .code-viewer-code * {
                    font-family: ui-monospace, SFMono-Regular, Menlo, monospace !important;
                    font-size: 0.8125rem !important;
                    line-height: 1.6 !important;
                }
                .token.comment, .token.prolog, .token.doctype, .token.cdata {
                    color: #9090A8 !important;
                    font-style: italic !important;
                }
                .token.keyword {
                    color: #A855F7 !important;
                    font-weight: 600 !important;
                }
                .token.string, .token.char, .token.attr-value {
                    color: #10B981 !important;
                }
                .token.number, .token.boolean {
                    color: #3B82F6 !important;
                }
                .token.function, .token.class-name, .token.builtin {
                    color: #F59E0B !important;
                }
                .token.operator, .token.punctuation {
                    color: #94A3B8 !important;
                }
                .token.variable, .token.property {
                    color: #F8FAFC !important;
                }
            """.trimIndent()
            document.head?.appendChild(style)
        }
        if (document.getElementById("prism-core-js") == null) {
            val scriptCore = document.createElement("script") as HTMLScriptElement
            scriptCore.id = "prism-core-js"
            scriptCore.src = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js"
            // The Lua grammar extends the global Prism object, so it may only load after the core has run.
            scriptCore.onload = {
                val scriptLua = document.createElement("script") as HTMLScriptElement
                scriptLua.id = "prism-lua-js"
                scriptLua.src = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-lua.min.js"
                document.head?.appendChild(scriptLua)
            }
            document.head?.appendChild(scriptCore)
        }
    } catch (_: Throwable) {
    }
}

fun highlightElement(element: Element) {
    try {
        val prism = window.asDynamic().Prism
        if (prism != undefined && prism != null) {
            prism.highlightElement(element)
        }
    } catch (_: Throwable) {
    }
}

fun copyToClipboard(text: String) {
    try {
        window.navigator.clipboard.writeText(text)
    } catch (_: Throwable) {
        val textarea = document.createElement("textarea") as HTMLTextAreaElement
        textarea.value = text
        document.body?.appendChild(textarea)
        textarea.select()
        document.execCommand("copy")
        document.body?.removeChild(textarea)
    }
}

@Composable
fun CodeViewer(
    code: String,
    language: String = "lua",
    showLineNumbers: Boolean = true,
    showCopyButton: Boolean = true,
    maxHeight: CSSNumericValue<out CSSUnitLengthOrPercentage>? = 16.cssRem,
    modifier: Modifier = Modifier,
    previousCode: String? = null,
    onFullscreenRequest: () -> Unit = {}
) {
    val theme = LocalAppTheme.current
    val s = strings()
    var monacoLoaded by remember { mutableStateOf(false) }
    var monacoFailed by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val win = window.asDynamic()
        val loadedHandler: (Any) -> Unit = {
            monacoLoaded = true
            monacoFailed = false
        }
        val failedHandler: (Any) -> Unit = {
            monacoLoaded = false
            monacoFailed = true
        }
        win.addEventListener("monaco-loaded", loadedHandler)
        win.addEventListener("monaco-failed", failedHandler)

        ensureMonacoLoaded(
            onLoaded = {
                monacoLoaded = true
                monacoFailed = false
            },
            attempt = 0,
            timeoutStartMs = Date.now().toLong()
        )

        onDispose {
            win.removeEventListener("monaco-loaded", loadedHandler)
            win.removeEventListener("monaco-failed", failedHandler)
        }
    }

    val canUseCodeEditor = monacoLoaded && !monacoFailed &&
        (language == "lua" || language == "luau") &&
        previousCode == null

    if (canUseCodeEditor) {
        val heightPx = 400.px
        CodeEditor(
            initialCode = code,
            modifier = modifier,
            readOnly = true,
            minHeight = heightPx,
            maxHeight = heightPx,
            languageId = if (language == "lua") "luau" else language,
            onFullscreenRequest = onFullscreenRequest
        )
        return
    }

    var copied by remember { mutableStateOf(false) }
    val lines = remember(code) { code.lines() }
    val diffLines = remember(code, previousCode) {
        if (previousCode != null) computeUnifiedDiff(previousCode, code) else null
    }

    val codeElements = remember { mutableListOf<HTMLElement>() }

    LaunchedEffect(code, previousCode) {
        ensurePrismLoaded()
        delay(30)
        codeElements.forEach { highlightElement(it) }
    }

    Div(CodeViewerContainerStyle.toModifier().then(modifier).toAttrs()) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                CodeViewerHeaderStyle.toModifier(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.375.cssRem)) {
                    Div(
                        Modifier
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.Bold)
                            .color(theme.colors.accent)
                            .toAttrs()
                    ) {
                        SpanText("🟣 ${if (language == "lua") "Luau" else language.uppercase()}")
                    }
                    Div(
                        Modifier
                            .fontSize(0.7.cssRem)
                            .color(theme.colors.textSecondary)
                            .toAttrs()
                    ) {
                        SpanText("• ${s.common.lines(lines.size)}")
                    }

                    if (diffLines != null) {
                        val addedCount = diffLines.count { it.type == DiffRowType.ADDED }
                        val removedCount = diffLines.count { it.type == DiffRowType.REMOVED }
                        Div(
                            Modifier
                                .fontSize(0.7.cssRem)
                                .fontWeight(FontWeight.Medium)
                                .color(theme.colors.textSecondary)
                                .margin(left = 0.5.cssRem)
                                .toAttrs()
                        ) {
                            SpanText("🟢 +$addedCount 🔴 −$removedCount")
                        }
                    }
                }

                Spacer()

                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.375.cssRem)) {
                    AppButton(
                        text = "",
                        iconLeft = "👁️",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = onFullscreenRequest
                    )

                    if (showCopyButton) {
                        AppButton(
                            text = if (copied) s.common.copied else s.common.copy,
                            iconLeft = if (copied) "✅" else "✨",
                            variant = AppButtonVariant.OUTLINE,
                            size = AppButtonSize.SM,
                            onClick = {
                                copyToClipboard(code)
                                copied = true
                                window.setTimeout({ copied = false }, 1500)
                            }
                        )
                    }
                }
            }

            Box(
                Modifier
                    .fillMaxWidth()
                    .then(if (maxHeight != null) Modifier.maxHeight(maxHeight) else Modifier)
                    .overflow(Overflow.Auto)
                    .backgroundColor(theme.colors.background)
            ) {
                if (diffLines == null) {
                    Row(Modifier.fillMaxWidth().minHeight(100.percent)) {
                        if (showLineNumbers && lines.size > 1) {
                            Column(
                                Modifier
                                    .padding(topBottom = 0.5.cssRem, leftRight = 0.5.cssRem)
                                    .backgroundColor(theme.colors.surface)
                                    .borderRight(1.px, LineStyle.Solid, theme.colors.border)
                                    .userSelect(UserSelect.None)
                            ) {
                                lines.indices.forEach { idx ->
                                    Div(
                                        Modifier
                                            .fontSize(0.8125.cssRem)
                                            .lineHeight(1.6)
                                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                                            .color(theme.colors.textSecondary.toRgb().copy(alpha = 140))
                                            .textAlign(TextAlign.Right)
                                            .minWidth(1.5.cssRem)
                                            .toAttrs()
                                    ) {
                                        SpanText("${idx + 1}")
                                    }
                                }
                            }
                        }

                        Box(
                            Modifier
                                .fillMaxWidth()
                                .padding(topBottom = 0.5.cssRem, leftRight = 0.75.cssRem)
                                .overflow(Overflow.Auto)
                        ) {
                            Pre(Modifier.classNames("code-viewer-pre").toAttrs()) {
                                Code(
                                    Modifier
                                        .classNames("code-viewer-code", "language-$language")
                                        .toAttrs {
                                            ref { element ->
                                                codeElements.add(element)
                                                highlightElement(element)
                                                onDispose { codeElements.remove(element) }
                                            }
                                        }
                                ) {
                                    Text(code)
                                }
                            }
                        }
                    }
                } else {
                    Column(Modifier.fillMaxWidth().minHeight(100.percent)) {
                        diffLines.forEach { diffLine ->
                            val rowBg = when (diffLine.type) {
                                DiffRowType.ADDED -> rgba(16, 185, 129, 0.10)
                                DiffRowType.REMOVED -> rgba(239, 68, 68, 0.08)
                                DiffRowType.UNCHANGED -> rgba(0, 0, 0, 0)
                            }
                            val prefixSign = when (diffLine.type) {
                                DiffRowType.ADDED -> "+"
                                DiffRowType.REMOVED -> "−"
                                DiffRowType.UNCHANGED -> "·"
                            }
                            val prefixColor = when (diffLine.type) {
                                DiffRowType.ADDED -> theme.colors.statusReady
                                DiffRowType.REMOVED -> theme.colors.statusFailed
                                DiffRowType.UNCHANGED -> theme.colors.textSecondary
                            }

                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .backgroundColor(rowBg),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(
                                    modifier = Modifier
                                        .backgroundColor(theme.colors.surface)
                                        .borderRight(1.px, LineStyle.Solid, theme.colors.border)
                                        .userSelect(UserSelect.None),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Div(
                                        Modifier
                                            .minWidth(2.5.cssRem)
                                            .padding(leftRight = 0.25.cssRem)
                                            .fontSize(0.75.cssRem)
                                            .lineHeight(1.6)
                                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                                            .color(theme.colors.textSecondary.toRgb().copy(alpha = 140))
                                            .textAlign(TextAlign.Right)
                                            .toAttrs()
                                    ) {
                                        SpanText(diffLine.oldLine?.toString() ?: "")
                                    }

                                    Div(
                                        Modifier
                                            .width(1.px)
                                            .height(1.6.cssRem)
                                            .backgroundColor(theme.colors.border)
                                            .toAttrs()
                                    )

                                    Div(
                                        Modifier
                                            .minWidth(2.5.cssRem)
                                            .padding(leftRight = 0.25.cssRem)
                                            .fontSize(0.75.cssRem)
                                            .lineHeight(1.6)
                                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                                            .color(theme.colors.textSecondary.toRgb().copy(alpha = 140))
                                            .textAlign(TextAlign.Right)
                                            .toAttrs()
                                    ) {
                                        SpanText(diffLine.newLine?.toString() ?: "")
                                    }

                                    Div(
                                        Modifier
                                            .width(1.px)
                                            .height(1.6.cssRem)
                                            .backgroundColor(theme.colors.border)
                                            .toAttrs()
                                    )

                                    Div(
                                        Modifier
                                            .minWidth(1.25.cssRem)
                                            .fontSize(0.875.cssRem)
                                            .lineHeight(1.6)
                                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                                            .fontWeight(FontWeight.Bold)
                                            .color(prefixColor)
                                            .textAlign(TextAlign.Center)
                                            .toAttrs()
                                    ) {
                                        SpanText(prefixSign)
                                    }
                                }

                                Box(
                                    Modifier
                                        .fillMaxWidth()
                                        .padding(leftRight = 0.75.cssRem)
                                        .overflow(Overflow.Auto)
                                ) {
                                    Pre(Modifier.classNames("code-viewer-pre").toAttrs()) {
                                        Code(
                                            Modifier
                                                .classNames("code-viewer-code", "language-$language")
                                                .toAttrs {
                                                    ref { element ->
                                                        codeElements.add(element)
                                                        highlightElement(element)
                                                        onDispose { codeElements.remove(element) }
                                                    }
                                                }
                                        ) {
                                            Text(diffLine.text)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
